import axios from 'axios';
import store from '@/store';

const getAuthHeader = (token) => {
    const devMode = store.state.user.devMode;

    if (devMode) {
        return { 'X-API-KEY': `${token}` };
    } else {
        return { Authorization: `Bearer ${token}` };
    }
};

const communicationChannelsService = {
    async fetchCommunicationChannels(page = 0, size = 20) {
        try {
            return store.dispatch('user/apiCall', async (token) => {
                return await axios.get(
                    `${process.env.VUE_APP_API_URL}/accounts/${store.state.user.user.account_id}/communication-channels`,
                    {
                        params: {
                            page,
                            size,
                        },
                        headers: getAuthHeader(token),
                    },
                );
            });
        } catch (error) {
            throw error;
        }
    },

    async deleteCommunicationChannel(CommunicationChannelId) {
        try {
            return store.dispatch('user/apiCall', async (token) => {
                return await axios.delete(
                    `${process.env.VUE_APP_API_URL}/accounts/${store.state.user.user.account_id}/communication-channels/${CommunicationChannelId}`,
                    {
                        headers: getAuthHeader(token),
                    },
                );
            });
        } catch (error) {
            throw error;
        }
    },

    async editCommunicationChannel(CommunicationChannelId, updateData) {
        try {
            return store.dispatch('user/apiCall', async (token) => {
                return await axios.post(
                    `${process.env.VUE_APP_API_URL}/accounts/${store.state.user.user.account_id}/communication-channels/${CommunicationChannelId}`,
                    updateData,
                    {
                        headers: getAuthHeader(token),
                    },
                );
            });
        } catch (error) {
            throw error;
        }
    },

    /**
     * Límite de mensajes de Meta de un canal de WhatsApp, leído por el backend con el token del
     * canal (el token ya no llega al navegador). 200 con `{ tier, limit }`, 204 si Meta no lo da.
     * @param {string} communicationChannelId - UUID del canal.
     */
    async fetchMessagingLimit(communicationChannelId) {
        return store.dispatch('user/apiCall', async (token) => {
            return await axios.get(
                `${process.env.VUE_APP_API_URL}/accounts/${store.state.user.user.account_id}/communication-channels/${communicationChannelId}/messaging-limit`,
                { headers: getAuthHeader(token) },
            );
        });
    },

    /**
     * Meta's monthly free tier of the number (1,000 delivered service messages per
     * month). 204 when the channel has no counter.
     */
    async fetchWhatsappFreeTier(communicationChannelId) {
        return store.dispatch('user/apiCall', async (token) => {
            return await axios.get(
                `${process.env.VUE_APP_API_URL}/accounts/${store.state.user.user.account_id}/communication-channels/${communicationChannelId}/whatsapp-free-tier`,
                { headers: getAuthHeader(token) },
            );
        });
    },

    async getCommunicationChannelByType(channelType, businessAccountId) {
        try {
            return store.dispatch('user/apiCall', async (token) => {
                return await axios.get(
                    `${process.env.VUE_APP_API_URL}/accounts/communication-channels/channel-type/${channelType.toUpperCase()}`,
                    {
                        params: {
                            businessAccountId,
                        },
                        headers: getAuthHeader(token),
                    },
                );
            });
        } catch (error) {
            throw error;
        }
    },

    /**
     * Sets a communication channel as the default for the account.
     * @param {string} communicationChannelId - The UUID of the channel to set as default.
     * @returns {Promise} Axios response promise.
     */
    async setDefaultChannel(communicationChannelId) {
        try {
            return store.dispatch('user/apiCall', async (token) => {
                return await axios.put(
                    `${process.env.VUE_APP_API_URL}/accounts/${store.state.user.user.account_id}/communication-channels/${communicationChannelId}/set-default`,
                    {},
                    {
                        headers: getAuthHeader(token),
                    },
                );
            });
        } catch (error) {
            throw error;
        }
    },

    /**
     * Refreshes the profile picture for a single WhatsApp channel.
     * @param {string} channelUuid - The UUID of the channel to refresh.
     * @returns {Promise} Axios response promise.
     */
    async refreshWhatsappChannelProfile(channelUuid) {
        return store.dispatch('user/apiCall', async (token) => {
            return await axios.post(
                `${process.env.VUE_APP_API_URL}/accounts/${store.state.user.user.account_id}/communication-channels/whatsapp/${channelUuid}/refresh-profile`,
                {},
                { headers: getAuthHeader(token) },
            );
        });
    },

    /**
     * Enables WhatsApp calling (SIP + voice line provisioning) for a channel.
     * @param {string} channelId - The UUID of the communication channel.
     * @returns {Promise} Axios response promise.
     */
    async enableCalling(channelId) {
        return store.dispatch('user/apiCall', async (token) => {
            return await axios.post(
                `${process.env.VUE_APP_API_URL}/accounts/${store.state.user.user.account_id}/communication-channels/${channelId}/enable-calling`,
                {},
                { headers: getAuthHeader(token) },
            );
        });
    },

    /**
     * Disables WhatsApp calling (SIP + voice line deprovisioning) for a channel.
     * @param {string} channelId - The UUID of the communication channel.
     * @returns {Promise} Axios response promise.
     */
    async disableCalling(channelId) {
        return store.dispatch('user/apiCall', async (token) => {
            return await axios.post(
                `${process.env.VUE_APP_API_URL}/accounts/${store.state.user.user.account_id}/communication-channels/${channelId}/disable-calling`,
                {},
                { headers: getAuthHeader(token) },
            );
        });
    },

    /**
     * Enables AI responses for a specific communication channel.
     * @param {string} channelId - The UUID of the communication channel.
     * @returns {Promise} Axios response promise.
     */
    async enableAi(channelId) {
        return store.dispatch('user/apiCall', async (token) => {
            return await axios.post(
                `${process.env.VUE_APP_API_URL}/accounts/${store.state.user.user.account_id}/communication-channels/${channelId}/enable-ai`,
                {},
                { headers: getAuthHeader(token) },
            );
        });
    },

    /**
     * Disables AI responses for a specific communication channel.
     * @param {string} channelId - The UUID of the communication channel.
     * @returns {Promise} Axios response promise.
     */
    async disableAi(channelId) {
        return store.dispatch('user/apiCall', async (token) => {
            return await axios.post(
                `${process.env.VUE_APP_API_URL}/accounts/${store.state.user.user.account_id}/communication-channels/${channelId}/disable-ai`,
                {},
                { headers: getAuthHeader(token) },
            );
        });
    },
};

export default communicationChannelsService;
